import React from 'react';
import { Controller } from 'react-hook-form';
import classNames from 'classnames';
import { AlurkerjaInputType } from '../../types/InputType';
import { Input } from '@/components/ui/input';

interface DateInputProps {
    props: AlurkerjaInputType;
}

export const DateInput = React.forwardRef<HTMLDivElement, DateInputProps>(({ props }, ref) => {
    const {
        item,
        form: { control },
    } = props;

    const getCurrentLanguage = () => {
        return 'en';
    };

    const getLabel = () => {
        const lang = getCurrentLanguage();
        if (typeof item.label === 'object' && item.label !== null) {
            return item.label[lang as keyof typeof item.label] || (item.label as any).en || '';
        }
        return item.label || '';
    };

    const getInputType = () => {
        if (item.form_field_type === 'INPUT_DATE') {
            return 'date';
        } else if (item.form_field_type === 'INPUT_DATETIME-LOCAL') {
            return 'datetime-local';
        }
        return 'text';
    };

    // Format value untuk ditampilkan di disabled input
    const formatDisplayValue = (value: any) => {
        if (!value) return '';

        // Jika value adalah Date object, format ke string DD-MM-YYYY
        if (value instanceof Date) {
            if (item.form_field_type === 'INPUT_DATE') {
                const year = value.getFullYear();
                const month = String(value.getMonth() + 1).padStart(2, '0');
                const day = String(value.getDate()).padStart(2, '0');
                return `${day}-${month}-${year}`;
            } else if (item.form_field_type === 'INPUT_DATETIME-LOCAL') {
                const year = value.getFullYear();
                const month = String(value.getMonth() + 1).padStart(2, '0');
                const day = String(value.getDate()).padStart(2, '0');
                const hours = String(value.getHours()).padStart(2, '0');
                const minutes = String(value.getMinutes()).padStart(2, '0');
                return `${day}-${month}-${year} ${hours}:${minutes}`;
            }
        }

        return value;
    };

    // Format value untuk HTML input (YYYY-MM-DD atau YYYY-MM-DDTHH:mm)
    const formatInputValue = (value: any) => {
        if (!value) return '';

        if (value instanceof Date) {
            if (item.form_field_type === 'INPUT_DATE') {
                const year = value.getFullYear();
                const month = String(value.getMonth() + 1).padStart(2, '0');
                const day = String(value.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            } else if (item.form_field_type === 'INPUT_DATETIME-LOCAL') {
                const year = value.getFullYear();
                const month = String(value.getMonth() + 1).padStart(2, '0');
                const day = String(value.getDate()).padStart(2, '0');
                const hours = String(value.getHours()).padStart(2, '0');
                const minutes = String(value.getMinutes()).padStart(2, '0');
                return `${year}-${month}-${day}T${hours}:${minutes}`;
            }
        }

        return value;
    };

    return (
        <div ref={ref} className="w-full">
            <label
                className={classNames('font-medium text-sm mb-1 block', {
                    'after:content-["*"] after:text-red-400 after:text-sm': item.constraints?.required,
                })}
                htmlFor={item.name}
            >
                {getLabel()}
            </label>
            <Controller
                name={item.name}
                control={control}
                defaultValue={item.defaultValue || ''}
                render={({ field, fieldState }) => {
                    // Jika disabled, tampilkan sebagai text input readonly
                    if (item.disabled) {
                        const displayValue = formatDisplayValue(field.value);
                        return (
                            <>
                                <Input
                                    type="text"
                                    id={item.name}
                                    value={displayValue}
                                    disabled={true}
                                    readOnly={true}
                                    className={classNames("w-full bg-gray-50", {
                                        "border-red-500": fieldState.error
                                    })}
                                />
                                {fieldState.error && (
                                    <p className="text-sm text-red-500 mt-1">{fieldState.error.message}</p>
                                )}
                            </>
                        );
                    }

                    // Jika tidak disabled, gunakan input date/datetime-local normal
                    const inputValue = formatInputValue(field.value);

                    return (
                        <>
                            <Input
                                {...field}
                                type={getInputType()}
                                id={item.name}
                                placeholder={item.placeholder}
                                value={inputValue}
                                onChange={(e) => {
                                    field.onChange(e.target.value);
                                }}
                                className={classNames("w-full", {
                                    "border-red-500": fieldState.error
                                })}
                            />
                            {fieldState.error && (
                                <p className="text-sm text-red-500 mt-1">{fieldState.error.message}</p>
                            )}
                        </>
                    );
                }}
            />
        </div>
    );
});

DateInput.displayName = 'DateInput';
